import { l as DocContext, q as createFunctionFromDefinition, k as jsxRuntimeExports } from "./doenetml-drNYjJiF.js";
import React__default, { useContext, useRef, useEffect } from "react";
import { u as useDoenetRenderer } from "./useDoenetRenderer-CDZbHBy-.js";
import { BoardContext, LINE_LAYER_OFFSET } from "./graph-uNmxqd5I.js";
import { s as styleToDash } from "./styleToDash-DWb71gbd.js";
import { g as getPatternFillAttributes } from "./fillPatterns-CRaVTk2g.js";
import { b as resolveCanvasColor } from "./styleColors-Cknqcr0q.js";
import { b as syncLabelMaskCssStyle, c as computeLabelMaskCssStyle, d as attachLabelHoverHighlight } from "./JSXGraphRenderer-BcR36ozd.js";
const regionBetweenCurves = React__default.memo(function RegionBetweenCurves(props) {
  let { id, SVs } = useDoenetRenderer(props);
  RegionBetweenCurves.ignoreActionsWithoutCore = () => true;
  const board = useContext(BoardContext);
  let curve1JXG = useRef(null);
  let curve2JXG = useRef(null);
  let regionJXG = useRef(null);
  let left = useRef(null);
  let right = useRef(null);
  const { darkMode } = useContext(DocContext) || {};
  useEffect(() => {
    return () => {
      deleteRegion();
    };
  }, []);
  function createRegion() {
    if (board === null) {
      return null;
    }
    if (!SVs.haveFunctions || SVs.boundaryValues.length !== 2 || !SVs.boundaryValues.every(Number.isFinite)) {
      return null;
    }
    [left.current, right.current] = [...SVs.boundaryValues].sort(
      (a, b) => a - b
    );
    let lineColor = darkMode === "dark" ? SVs.selectedStyle.lineColorDarkMode : SVs.selectedStyle.lineColor;
    const fillAttributes = getPatternFillAttributes({
      defsEl: board.renderer.defs,
      boardId: board.container.id,
      fillStyle: SVs.selectedStyle.fillStyle ?? "solid",
      fillColor: darkMode === "dark" ? SVs.selectedStyle.fillColorDarkMode : SVs.selectedStyle.fillColor,
      fillOpacity: SVs.selectedStyle.fillOpacity,
      canvasColor: resolveCanvasColor(darkMode),
      fillPatternOpacity: SVs.selectedStyle.fillPatternOpacity
    });
    let jsxAttributes = {
      name: SVs.labelForGraph,
      visible: !SVs.hidden,
      withLabel: SVs.labelForGraph !== "",
      fixed: true,
      layer: 10 * SVs.layer + LINE_LAYER_OFFSET,
      strokeColor: lineColor,
      strokeOpacity: SVs.selectedStyle.lineOpacity,
      strokeWidth: SVs.selectedStyle.lineWidth,
      dash: styleToDash(SVs.selectedStyle.lineStyle, SVs.dashed),
      fillColor: fillAttributes.fillColor,
      fillOpacity: fillAttributes.fillOpacity,
      highlight: false
    };
    jsxAttributes.label = {
      highlight: false,
      ...computeLabelMaskCssStyle({
        layer: SVs.layer,
        masked: SVs.maskLabel
      }),
      highlightStrokeOpacity: 1
    };
    let f1 = createFunctionFromDefinition(SVs.fDefinitions[0]);
    let f2 = createFunctionFromDefinition(SVs.fDefinitions[1]);
    curve1JXG.current = board.create("functiongraph", f1, {
      visible: false
    });
    curve2JXG.current = board.create("functiongraph", f2, {
      visible: false
    });
    var region = board.create("curve", [[], []], jsxAttributes);
    region.updateDataArray = function() {
      var x, y;
      const c1 = curve1JXG.current;
      const c2 = curve2JXG.current;
      const leftVal = left.current;
      const rightVal = right.current;
      x = [leftVal];
      y = [c1.Y(leftVal)];
      for (let pt of c1.points) {
        if (leftVal <= pt.usrCoords[1] && pt.usrCoords[1] <= rightVal) {
          x.push(pt.usrCoords[1]);
          y.push(pt.usrCoords[2]);
        }
      }
      x.push(rightVal);
      y.push(c1.Y(rightVal));
      x.push(rightVal);
      y.push(c2.Y(rightVal));
      for (let pt of [...c2.points].reverse()) {
        if (leftVal <= pt.usrCoords[1] && pt.usrCoords[1] <= rightVal) {
          x.push(pt.usrCoords[1]);
          y.push(pt.usrCoords[2]);
        }
      }
      x.push(leftVal);
      y.push(c2.Y(leftVal));
      x.push(leftVal);
      y.push(c1.Y(leftVal));
      if (SVs.flipFunctions) {
        this.dataX = y;
        this.dataY = x;
      } else {
        this.dataX = x;
        this.dataY = y;
      }
    };
    attachLabelHoverHighlight({
      hoverTargetJXG: region,
      getLabelJXG: () => regionJXG.current?.label,
      ...computeLabelMaskCssStyle({
        layer: SVs.layer,
        masked: SVs.maskLabel
      }),
      board
    });
    return region;
  }
  function deleteRegion() {
    if (regionJXG.current) {
      board?.removeObject(regionJXG.current);
      regionJXG.current = null;
      if (curve1JXG.current) {
        board?.removeObject(curve1JXG.current);
        curve1JXG.current = null;
      }
      if (curve2JXG.current) {
        board?.removeObject(curve2JXG.current);
        curve2JXG.current = null;
      }
    }
  }
  if (board) {
    if (regionJXG.current === null) {
      regionJXG.current = createRegion();
    } else if (!SVs.haveFunctions || SVs.boundaryValues.length !== 2 || !SVs.boundaryValues.every(Number.isFinite)) {
      deleteRegion();
    } else {
      let f1 = createFunctionFromDefinition(SVs.fDefinitions[0]);
      let f2 = createFunctionFromDefinition(SVs.fDefinitions[1]);
      if (curve1JXG.current) {
        curve1JXG.current.Y = f1;
      }
      if (curve2JXG.current) {
        curve2JXG.current.Y = f2;
      }
      regionJXG.current.visProp["visible"] = !SVs.hidden;
      regionJXG.current.visPropCalc["visible"] = !SVs.hidden;
      [left.current, right.current] = [...SVs.boundaryValues].sort(
        (a, b) => a - b
      );
      let layer = 10 * SVs.layer + LINE_LAYER_OFFSET;
      let layerChanged = regionJXG.current.visProp.layer !== layer;
      if (layerChanged) {
        regionJXG.current.setAttribute({ layer });
      }
      let lineColor = darkMode === "dark" ? SVs.selectedStyle.lineColorDarkMode : SVs.selectedStyle.lineColor;
      if (regionJXG.current.visProp.strokecolor !== lineColor) {
        regionJXG.current.visProp.strokecolor = lineColor;
        regionJXG.current.visProp.highlightstrokecolor = lineColor;
      }
      if (regionJXG.current.visProp.strokeopacity !== SVs.selectedStyle.lineOpacity) {
        regionJXG.current.visProp.strokeopacity = SVs.selectedStyle.lineOpacity;
      }
      let newDash = styleToDash(SVs.selectedStyle.lineStyle, SVs.dashed);
      if (regionJXG.current.visProp.dash !== newDash) {
        regionJXG.current.visProp.dash = newDash;
      }
      if (regionJXG.current.visProp.strokewidth !== SVs.selectedStyle.lineWidth) {
        regionJXG.current.visProp.strokewidth = SVs.selectedStyle.lineWidth;
      }
      const fillAttributes = getPatternFillAttributes({
        defsEl: board.renderer.defs,
        boardId: board.container.id,
        fillStyle: SVs.selectedStyle.fillStyle ?? "solid",
        fillColor: darkMode === "dark" ? SVs.selectedStyle.fillColorDarkMode : SVs.selectedStyle.fillColor,
        fillOpacity: SVs.selectedStyle.fillOpacity,
        canvasColor: resolveCanvasColor(darkMode),
        fillPatternOpacity: SVs.selectedStyle.fillPatternOpacity
      });
      if (regionJXG.current.visProp.fillcolor !== fillAttributes.fillColor) {
        regionJXG.current.visProp.fillcolor = fillAttributes.fillColor;
      }
      if (regionJXG.current.visProp.fillopacity !== fillAttributes.fillOpacity) {
        regionJXG.current.visProp.fillopacity = fillAttributes.fillOpacity;
      }
      if (regionJXG.current.hasLabel && regionJXG.current.label) {
        syncLabelMaskCssStyle(regionJXG.current.label, SVs.layer, {
          maskLabel: SVs.maskLabel
        });
      }
      regionJXG.current.needsUpdate = true;
      regionJXG.current.fullUpdate();
      board.update();
      board.fullUpdate();
      board.updateRenderer();
    }
    return /* @__PURE__ */ jsxRuntimeExports.jsx(jsxRuntimeExports.Fragment, { children: /* @__PURE__ */ jsxRuntimeExports.jsx("span", { id }) });
  }
  if (SVs.hidden) {
    return null;
  }
  return /* @__PURE__ */ jsxRuntimeExports.jsx(jsxRuntimeExports.Fragment, { children: /* @__PURE__ */ jsxRuntimeExports.jsx("span", { id }) });
});
export {
  regionBetweenCurves as default
};
